import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, CardBody, Table, Nav, NavItem, NavLink } from 'reactstrap';
import Preloader from '../../components/common/Preloader';
import { Link, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import Flatpickr from "react-flatpickr";
import SEO from '../../components/common/SEO';
import { formatDate } from '../../utils/dateUtils';
import { useVendorAuth } from '../../context/VendorAuthContext';
import ActiveSubscriptionSummary from '../component/ActiveSubscriptionCard';

const DEFAULT_STATS = {
    holidayPackages: 0,
    hotels: 0,
    cabs: 0,
    yatra: 0,
    hospitals: 0,
    doctors: 0
};

const statValue = (value) => value === undefined || value === null || value === '' ? 0 : value;

const Dashboard = () => {
    const navigate = useNavigate();
    const { vendor, updateVendor } = useVendorAuth();
    const registrationStep = Number(vendor?.last_registration_step ?? 0);

    const [stats, setStats] = useState(DEFAULT_STATS);
    const [enquiries, setEnquiries] = useState([]);
    const [cityStats, setCityStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [enquiriesLoading, setEnquiriesLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);

    // Wizard States
    const [wizardStep, setWizardStep] = useState(1);
    const [wizardFormData, setWizardFormData] = useState({
        company_name: '',
        company_address: '',
        company_landmark: '',
        company_city: '',
        company_state: '',
        company_pincode: '',
        operational_address: '',
        operational_landmark: '',
        operational_city: '',
        operational_state: '',
        operational_pincode: '',
        whatsapp_no: '',
        kyc_type: '',
        kyc_doc_number: '',
        kyc_other_document: '',
        business_categories: [],
        other_category: ''
    });
    const [isSameAddress, setIsSameAddress] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [kycFile, setKycFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [kycPreview, setKycPreview] = useState(null);
    const [wizardLoading, setWizardLoading] = useState(false);
    const [wizardError, setWizardError] = useState('');
    const [wizardSuccess, setWizardSuccess] = useState('');

    const fetchFreshVendorData = useCallback(async (vendorId) => {
        if (!vendorId) return null;
        try {
            const token = localStorage.getItem('authToken');
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.get(`${apiBaseUrl}/vendors/${vendorId}/details`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.user) {
                const freshData = {
                    ...response.data.user,
                    is_impersonated: vendor?.is_impersonated
                };
                setUser(freshData);
                localStorage.setItem('userInfo', JSON.stringify(freshData));
                const currentStep = Number(vendor?.last_registration_step ?? 0);
                const freshStep = Number(freshData?.last_registration_step ?? 0);
                if (freshStep !== currentStep) {
                    updateVendor(freshData);
                }
                return freshData;
            }
        } catch (error) {
            console.error('Dashboard - Error fetching fresh vendor data:', error);
        }
        return null;
    }, [updateVendor, vendor]);

    useEffect(() => {
        let isMounted = true;

        const bootstrapVendorView = async () => {
            if (!vendor) {
                if (isMounted) {
                    setUser(null);
                    setLoading(false);
                    setEnquiriesLoading(false);
                }
                return;
            }

            if (isMounted) {
                setUser(vendor);
            }

            let effectiveVendor = vendor;
            try {
                const freshVendor = await fetchFreshVendorData(vendor.id);
                if (freshVendor) {
                    effectiveVendor = freshVendor;
                }
            } catch (error) {
                console.error('Dashboard - failed to refresh vendor profile:', error);
            }

            const effectiveStep = Number(effectiveVendor?.last_registration_step ?? 0);
            if (effectiveVendor?.is_impersonated || effectiveStep >= 12) {
                fetchStats(effectiveVendor.id);
                fetchEnquiries(effectiveVendor.id, 'all');
                const today = new Date();
                fetchCityStats(effectiveVendor.id, [today, today]);
            } else if (isMounted) {
                if (effectiveStep < 6) {
                    navigate('/admin/kyc-verification');
                } else {
                    navigate('/admin/profile-setup');
                }
            }
        };

        bootstrapVendorView();

        return () => {
            isMounted = false;
        };
    }, [vendor, fetchFreshVendorData, navigate]);

    const getParsedCategories = (catField) => {
        if (!catField) return [];
        if (Array.isArray(catField)) return catField;
        try {
            let parsed = JSON.parse(catField);
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    useEffect(() => {
        if (vendor && registrationStep < 9) {
            const step = registrationStep;
            if (step <= 2) {
                setWizardStep(1);
            } else if (step === 4) {
                setWizardStep(2);
            } else if (step === 5) {
                setWizardStep(3);
            } else {
                setWizardStep(1);
            }

            setWizardFormData({
                company_name: vendor.company_name || '',
                company_address: vendor.company_address || '',
                company_landmark: vendor.company_landmark || '',
                company_city: vendor.company_city || '',
                company_state: vendor.company_state || '',
                company_pincode: vendor.company_pincode || '',
                operational_address: vendor.operational_address || '',
                operational_landmark: vendor.operational_landmark || '',
                operational_city: vendor.operational_city || '',
                operational_state: vendor.operational_state || '',
                operational_pincode: vendor.operational_pincode || '',
                whatsapp_no: vendor.whatsapp_no || vendor.phone || '',
                kyc_type: vendor.kyc_type || '',
                kyc_doc_number: vendor.kyc_doc_number || '',
                kyc_other_document: vendor.kyc_other_document || '',
                business_categories: getParsedCategories(vendor.business_categories),
                other_category: vendor.other_category || ''
            });

            if (vendor.company_logo_path) {
                setLogoPreview(vendor.company_logo_path);
            }
            if (vendor.kyc_file_path) {
                setKycPreview(vendor.kyc_file_path);
            }

            if (
                vendor.operational_address &&
                vendor.operational_address === vendor.company_address &&
                vendor.operational_landmark === vendor.company_landmark &&
                vendor.operational_city === vendor.company_city &&
                vendor.operational_state === vendor.company_state &&
                vendor.operational_pincode === vendor.company_pincode
            ) {
                setIsSameAddress(true);
            }
        }
    }, [vendor, registrationStep]);

    const handleAddressCheckboxChange = (e) => {
        const checked = e.target.checked;
        setIsSameAddress(checked);
        if (checked) {
            setWizardFormData(prev => ({
                ...prev,
                operational_address: prev.company_address,
                operational_landmark: prev.company_landmark,
                operational_city: prev.company_city,
                operational_state: prev.company_state,
                operational_pincode: prev.company_pincode
            }));
        } else {
            setWizardFormData(prev => ({
                ...prev,
                operational_address: '',
                operational_landmark: '',
                operational_city: '',
                operational_state: '',
                operational_pincode: ''
            }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setWizardFormData(prev => ({ ...prev, [name]: value }));

        if (isSameAddress && name.startsWith('company_')) {
            const operationalField = name.replace('company_', 'operational_');
            setWizardFormData(prev => ({ ...prev, [operationalField]: value }));
        }
    };

    const handleLogoChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleKycFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setKycFile(file);
            setKycPreview(URL.createObjectURL(file));
        }
    };

    const handleCategoryChange = (catName, checked) => {
        setWizardFormData(prev => {
            let list = [...prev.business_categories];
            if (checked) {
                if (!list.includes(catName)) list.push(catName);
            } else {
                list = list.filter(c => c !== catName);
            }
            return { ...prev, business_categories: list };
        });
    };

    const handlePrevStep = () => {
        if (wizardStep > 1) {
            setWizardStep(prev => prev - 1);
            setWizardError('');
            setWizardSuccess('');
        }
    };

    const handleStepSubmit = async (e) => {
        e.preventDefault();
        setWizardError('');
        setWizardSuccess('');
        setWizardLoading(true);

        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('authToken');

        try {
            const data = new FormData();

            if (wizardStep === 1) {
                if (!wizardFormData.company_name.trim()) throw new Error("Company Name is required");
                if (!wizardFormData.company_address.trim()) throw new Error("Registered Address is required");
                if (!wizardFormData.company_city.trim()) throw new Error("City is required");
                if (!wizardFormData.company_state.trim()) throw new Error("State is required");
                if (!wizardFormData.company_pincode.trim()) throw new Error("Pincode is required");
                if (!isSameAddress && !wizardFormData.operational_address.trim()) throw new Error("Operational Address is required");
                if (!wizardFormData.whatsapp_no.trim()) throw new Error("Whatsapp number is required");

                data.append('completed_step', '4');
                data.append('company_name', wizardFormData.company_name);
                data.append('company_address', wizardFormData.company_address);
                data.append('company_landmark', wizardFormData.company_landmark);
                data.append('company_city', wizardFormData.company_city);
                data.append('company_state', wizardFormData.company_state);
                data.append('company_pincode', wizardFormData.company_pincode);
                data.append('operational_address', isSameAddress ? wizardFormData.company_address : wizardFormData.operational_address);
                data.append('operational_landmark', isSameAddress ? wizardFormData.company_landmark : wizardFormData.operational_landmark);
                data.append('operational_city', isSameAddress ? wizardFormData.company_city : wizardFormData.operational_city);
                data.append('operational_state', isSameAddress ? wizardFormData.company_state : wizardFormData.operational_state);
                data.append('operational_pincode', isSameAddress ? wizardFormData.company_pincode : wizardFormData.operational_pincode);
                data.append('whatsapp_no', wizardFormData.whatsapp_no);

                if (logoFile) {
                    data.append('companyLogo', logoFile);
                }
            } else if (wizardStep === 2) {
                if (!wizardFormData.kyc_type) throw new Error("KYC Document Type is required");
                if (!wizardFormData.kyc_doc_number.trim()) throw new Error("Document Number is required");
                if (wizardFormData.kyc_type === 'Other' && !wizardFormData.kyc_other_document.trim()) throw new Error("Please specify other document name");
                if (!kycFile && (!vendor || !vendor.kyc_file_path)) throw new Error("Please upload the KYC document file");

                data.append('completed_step', '5');
                data.append('kycType', wizardFormData.kyc_type);
                data.append('kyc_doc_number', wizardFormData.kyc_doc_number);
                if (wizardFormData.kyc_type === 'Other') {
                    data.append('kyc_other_document', wizardFormData.kyc_other_document);
                }
                if (kycFile) {
                    data.append('kycFile', kycFile);
                }
            } else if (wizardStep === 3) {
                if (wizardFormData.business_categories.length === 0) throw new Error("Select at least one category");
                if (wizardFormData.business_categories.includes('Other') && !wizardFormData.other_category.trim()) throw new Error("Please specify the other category");

                data.append('completed_step', '6');
                data.append('is_final_step', 'true');
                data.append('business_categories', JSON.stringify(wizardFormData.business_categories));
                if (wizardFormData.business_categories.includes('Other')) {
                    data.append('other_category', wizardFormData.other_category);
                }
            }

            const response = await axios.put(
                `${apiBaseUrl}/vendors/${vendor.id}/registration-details`,
                data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data && response.data.user) {
                updateVendor(response.data.user);
                setWizardSuccess("Step saved successfully!");
                if (wizardStep < 3) {
                    setWizardStep(prev => prev + 1);
                } else {
                    setWizardSuccess("KYC Completed successfully! Loading Dashboard...");
                }
            }
        } catch (error) {
            console.error("KYC Wizard Step submit error:", error);
            setWizardError(error.response?.data?.message || error.message || "Something went wrong.");
        } finally {
            setWizardLoading(false);
        }
    };

    const fetchStats = async (userId) => {
        try {
            const token = localStorage.getItem('authToken');
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.get(`${apiBaseUrl}/vendors/${userId}/dashboard-stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setStats({
                    ...DEFAULT_STATS,
                    ...(response.data.stats || {})
                });
            }
        } catch (error) {
            console.error("Dashboard - Error fetching dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEnquiries = async (userId, category) => {
        setEnquiriesLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await axios.get(`${apiBaseUrl}/vendors/${userId}/recent-enquiries`, {
                params: { category },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEnquiries(response.data.enquiries);
            }
        } catch (error) {
            console.error("Dashboard - Error fetching enquiries:", error);
        } finally {
            setEnquiriesLoading(false);
        }
    };

    const fetchCityStats = async (userId, dates) => {
        try {
            const token = localStorage.getItem('authToken');
            const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const params = {};
            if (dates && dates[0] && dates[1]) {
                // Adjust for timezone to get local date string
                const formatDate = (date) => {
                    const d = new Date(date);
                    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                    return d.toISOString().split('T')[0];
                };
                params.startDate = formatDate(dates[0]);
                params.endDate = formatDate(dates[1]);
            }

            const response = await axios.get(`${apiBaseUrl}/vendors/${userId}/city-stats`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCityStats(response.data.stats);
            }
        } catch (error) {
            console.error("Dashboard - Error fetching city stats:", error);
        }
    };

    const onDateChange = (selectedDates) => {
        if (selectedDates.length === 2) {
            setDateRange(selectedDates);
            if (user) {
                fetchCityStats(user.id, selectedDates);
            }
        } else if (selectedDates.length === 0) {
            setDateRange([null, null]);
            if (user) {
                fetchCityStats(user.id, [null, null]);
            }
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (user) {
            fetchEnquiries(user.id, tab);
        }
    };

    const statCards = [
        {
            title: "Holiday Packages",
            value: statValue(stats.holidayPackages),
            icon: "ri-suitcase-2-line",
            gradient: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            badge: "Active Plans"
        },
        {
            title: "Hotels",
            value: statValue(stats.hotels),
            icon: "ri-hotel-line",
            gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
            badge: "Properties"
        },
        {
            title: "Cab Services",
            value: statValue(stats.cabs),
            icon: "ri-taxi-line",
            gradient: "linear-gradient(135deg, #10b981 0%, #3b82f6 100%)",
            badge: "Fleet"
        },
        {
            title: "Yatra Packages",
            value: statValue(stats.yatra),
            icon: "ri-map-pin-line",
            gradient: "linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)",
            badge: "Yatras"
        },
        {
            title: "Medical Tourism",
            value: `${statValue(stats.doctors)} / ${statValue(stats.hospitals)}`,
            icon: "ri-hospital-line",
            gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            badge: "Docs & Hos"
        }
    ];

    if (loading) {
        return <Preloader />;
    }

    return (
        <Container fluid>
            <style>
                {`
                    .flatpickr-calendar {
                        background: #fff !important;
                        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
                        border: 1px solid #e2e8f0 !important;
                        border-radius: 12px !important;
                    }
                    @media (min-width: 1200px) {
                        .dashboard-stat-col {
                            flex: 0 0 20%;
                            max-width: 20%;
                        }
                    }
                    .flatpickr-months {
                        background: #475569 !important;
                        border-radius: 12px 12px 0 0 !important;
                    }
                    .flatpickr-month {
                        background: transparent !important;
                        color: #fff !important;
                        fill: #fff !important;
                    }
                    .flatpickr-current-month .flatpickr-monthDropdown-months {
                        color: #fff !important;
                    }
                    .flatpickr-current-month input.cur-year {
                        color: #fff !important;
                    }
                    .flatpickr-prev-month, .flatpickr-next-month {
                        color: #fff !important;
                        fill: #fff !important;
                    }
                    .flatpickr-weekdays {
                        background: #475569 !important;
                    }
                    span.flatpickr-weekday {
                        background: #475569 !important;
                        color: #fff !important;
                    }
                    .flatpickr-day.selected, .flatpickr-day.startRange, .flatpickr-day.endRange {
                        background: #475569 !important;
                        border-color: #475569 !important;
                        color: #fff !important;
                    }
                    .flatpickr-day.inRange, .flatpickr-day:hover, .flatpickr-day.focused {
                        background: #e2e8f0 !important;
                        border-color: transparent !important;
                        color: #1e293b !important;
                    }
                    .hover-primary:hover {
                        color: #2563eb !important;
                        text-decoration: underline !important;
                    }
                    .upload-container label {
                        transition: all 0.2s ease-in-out;
                    }
                    .upload-container label:hover {
                        border-color: #0f172a !important;
                        background-color: #f8fafc !important;
                    }
                `}
            </style>
            {/* <SEO title="Vendor Dashboard | Mangoloops" /> */}

            {vendor && registrationStep < 6 && !vendor.is_impersonated ? (
                // KYC PROGRESS WIZARD
                <div className="py-4">
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <div className="alert alert-warning border-0 rounded-4 mb-4 p-4 shadow-sm d-flex align-items-start">
                                <div className="avatar-sm me-3 flex-shrink-0">
                                    <div className="avatar-title bg-warning-subtle text-warning rounded-circle fs-24 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                        <i className="ri-error-warning-fill"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="alert-heading mb-1 fw-bold text-dark">KYC Verification Required</h5>
                                    <p className="mb-0 text-muted small">Welcome, <strong>{vendor.name}</strong>! To start receiving business enquiries, listing your packages, and managing listings, you must complete your KYC verification profile. Fill in the three steps below to activate your account.</p>
                                </div>
                            </div>

                            <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                                <CardBody className="p-5">
                                    {/* Progress Bar */}
                                    <div className="kyc-progress-bar-container mb-5" style={{ position: 'relative' }}>
                                        <div className="d-flex justify-content-between position-relative">
                                            <div className="progress-line" style={{
                                                position: 'absolute',
                                                top: '24px',
                                                left: '10%',
                                                right: '10%',
                                                height: '4px',
                                                backgroundColor: '#e2e8f0',
                                                zIndex: 1
                                            }}>
                                                <div className="progress-line-fill" style={{
                                                    width: wizardStep === 1 ? '0%' : wizardStep === 2 ? '50%' : '100%',
                                                    height: '100%',
                                                    backgroundColor: '#0f172a',
                                                    transition: 'width 0.3s ease'
                                                }}></div>
                                            </div>

                                            {/* Step 1 */}
                                            <div className="step-item text-center" style={{ zIndex: 2 }}>
                                                <div className={`step-icon rounded-circle d-flex align-items-center justify-content-center shadow-sm ${wizardStep >= 1 ? 'bg-dark text-white' : 'bg-light text-muted'}`} style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    margin: '0 auto 10px',
                                                    border: '3px solid #fff',
                                                    fontSize: '18px',
                                                    transition: 'all 0.3s'
                                                }}>
                                                    <i className="ri-building-line"></i>
                                                </div>
                                                <span className={`fw-bold small ${wizardStep >= 1 ? 'text-dark' : 'text-muted'}`}>Company Info</span>
                                            </div>

                                            {/* Step 2 */}
                                            <div className="step-item text-center" style={{ zIndex: 2 }}>
                                                <div className={`step-icon rounded-circle d-flex align-items-center justify-content-center shadow-sm ${wizardStep >= 2 ? 'bg-dark text-white' : 'bg-light text-muted'}`} style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    margin: '0 auto 10px',
                                                    border: '3px solid #fff',
                                                    fontSize: '18px',
                                                    transition: 'all 0.3s'
                                                }}>
                                                    <i className="ri-shield-user-line"></i>
                                                </div>
                                                <span className={`fw-bold small ${wizardStep >= 2 ? 'text-dark' : 'text-muted'}`}>KYC Documents</span>
                                            </div>

                                            {/* Step 3 */}
                                            <div className="step-item text-center" style={{ zIndex: 2 }}>
                                                <div className={`step-icon rounded-circle d-flex align-items-center justify-content-center shadow-sm ${wizardStep >= 3 ? 'bg-dark text-white' : 'bg-light text-muted'}`} style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    margin: '0 auto 10px',
                                                    border: '3px solid #fff',
                                                    fontSize: '18px',
                                                    transition: 'all 0.3s'
                                                }}>
                                                    <i className="ri-price-tag-3-line"></i>
                                                </div>
                                                <span className={`fw-bold small ${wizardStep >= 3 ? 'text-dark' : 'text-muted'}`}>Categories</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Success/Error Notifications */}
                                    {wizardError && (
                                        <div className="alert alert-danger border-0 rounded-3 mb-4 d-flex align-items-center py-3">
                                            <i className="ri-error-warning-fill fs-20 me-2"></i>
                                            <span className="fw-medium">{wizardError}</span>
                                        </div>
                                    )}
                                    {wizardSuccess && (
                                        <div className="alert alert-success border-0 rounded-3 mb-4 d-flex align-items-center py-3">
                                            <i className="ri-checkbox-circle-fill fs-20 me-2"></i>
                                            <span className="fw-medium">{wizardSuccess}</span>
                                        </div>
                                    )}

                                    {/* Wizard Forms */}
                                    {wizardStep === 1 && (
                                        <form onSubmit={handleStepSubmit}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <h5 className="fw-bold mb-3 text-dark">Step 1: Complete Company Information</h5>
                                                    <hr className="my-3 text-muted opacity-25" />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-muted small">COMPANY NAME</label>
                                                    <input type="text" className="form-control py-2" name="company_name" value={wizardFormData.company_name} onChange={handleInputChange} placeholder="Enter company name" required />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-muted small">WHATSAPP NUMBER</label>
                                                    <input type="text" className="form-control py-2" name="whatsapp_no" value={wizardFormData.whatsapp_no} onChange={handleInputChange} placeholder="Enter Whatsapp number" required />
                                                </div>

                                                <div className="col-lg-6">
                                                    <div className="p-4 rounded-4 bg-light-subtle border border-light h-100">
                                                        <h6 className="fw-bold text-dark mb-4 d-flex align-items-center">
                                                            <i className="ri-map-pin-fill me-2 text-dark"></i> Registered Office Address
                                                        </h6>
                                                        <div className="row g-3">
                                                            <div className="col-12">
                                                                <label className="form-label small fw-bold text-muted">ADDRESS LINE</label>
                                                                <textarea className="form-control" rows="2" name="company_address" value={wizardFormData.company_address} onChange={handleInputChange} placeholder="Enter registered address" required></textarea>
                                                            </div>
                                                            <div className="col-12">
                                                                <label className="form-label small fw-bold text-muted">LANDMARK</label>
                                                                <input type="text" className="form-control" name="company_landmark" value={wizardFormData.company_landmark} onChange={handleInputChange} placeholder="Ex: Near City Mall" />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">CITY</label>
                                                                <input type="text" className="form-control" name="company_city" value={wizardFormData.company_city} onChange={handleInputChange} placeholder="City" required />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">STATE</label>
                                                                <input type="text" className="form-control" name="company_state" value={wizardFormData.company_state} onChange={handleInputChange} placeholder="State" required />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">PINCODE</label>
                                                                <input type="text" className="form-control" name="company_pincode" value={wizardFormData.company_pincode} onChange={handleInputChange} placeholder="Zip" required />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-lg-6">
                                                    <div className="p-4 rounded-4 bg-light-subtle border border-light h-100">
                                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                                            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
                                                                <i className="ri-map-pin-2-fill me-2 text-dark"></i> Operational Office Address
                                                            </h6>
                                                            <div className="form-check form-switch">
                                                                <input className="form-check-input" type="checkbox" id="wizardIsSameAddress" checked={isSameAddress} onChange={handleAddressCheckboxChange} />
                                                                <label className="form-check-label small fw-bold" htmlFor="wizardIsSameAddress">SAME AS REGISTERED</label>
                                                            </div>
                                                        </div>
                                                        <div className="row g-3">
                                                            <div className="col-12">
                                                                <label className="form-label small fw-bold text-muted">ADDRESS LINE</label>
                                                                <textarea className="form-control" rows="2" name="operational_address" value={wizardFormData.operational_address} onChange={handleInputChange} placeholder="Enter operational address" disabled={isSameAddress} required={!isSameAddress}></textarea>
                                                            </div>
                                                            <div className="col-12">
                                                                <label className="form-label small fw-bold text-muted">LANDMARK</label>
                                                                <input type="text" className="form-control" name="operational_landmark" value={wizardFormData.operational_landmark} onChange={handleInputChange} placeholder="Enter landmark" disabled={isSameAddress} />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">CITY</label>
                                                                <input type="text" className="form-control" name="operational_city" value={wizardFormData.operational_city} onChange={handleInputChange} placeholder="City" disabled={isSameAddress} required={!isSameAddress} />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">STATE</label>
                                                                <input type="text" className="form-control" name="operational_state" value={wizardFormData.operational_state} onChange={handleInputChange} placeholder="State" disabled={isSameAddress} required={!isSameAddress} />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label small fw-bold text-muted">PINCODE</label>
                                                                <input type="text" className="form-control" name="operational_pincode" value={wizardFormData.operational_pincode} onChange={handleInputChange} placeholder="Zip" disabled={isSameAddress} required={!isSameAddress} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 mt-4">
                                                    <div className="p-4 rounded-4 bg-light shadow-sm">
                                                        <h6 className="fw-bold text-dark mb-4 d-flex align-items-center">
                                                            <i className="ri-image-2-fill me-2"></i> Business Branding
                                                        </h6>
                                                        <div className="row g-4 align-items-center">
                                                            <div className="col-md-6">
                                                                <label className="form-label fw-bold text-muted small">COMPANY LOGO</label>
                                                                <div className="upload-container position-relative">
                                                                    <input type="file" className="form-control d-none" id="wizardLogoUpload" onChange={handleLogoChange} accept="image/*" />
                                                                    <label htmlFor="wizardLogoUpload" className="d-block p-4 border-2 border-dashed rounded-4 text-center cursor-pointer bg-white transition-all">
                                                                        <div className="avatar-sm mx-auto mb-3">
                                                                            <div className="avatar-title bg-light text-dark rounded-circle shadow-sm fs-20" style={{ width: '40px', height: '40px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                <i className="ri-image-add-line"></i>
                                                                            </div>
                                                                        </div>
                                                                        <h6 className="fs-15 fw-bold mb-1 text-dark">Upload Logo</h6>
                                                                        <p className="text-muted small mb-0">Drag and drop or browse</p>
                                                                        <div className="mt-2">
                                                                            <span className="badge text-dark bg-light px-2 py-1" style={{ border: '1px solid #cbd5e1' }}>JPG, PNG or WebP</span>
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                                {logoFile && (
                                                                    <div className="alert alert-dark mt-3 mb-0 border-0 shadow-sm d-flex align-items-center py-2 text-white bg-dark">
                                                                        <i className="ri-checkbox-circle-fill fs-16 me-2"></i>
                                                                        <span className="fw-medium small">Selected: {logoFile.name}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="col-md-6 text-center">
                                                                {logoPreview ? (
                                                                    <div className="d-inline-block p-3 bg-white rounded-3 shadow-sm border border-light">
                                                                        <img src={logoPreview} alt="Logo" className="avatar-lg rounded-circle shadow-sm border p-1 bg-light" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                                                                        <p className="mb-0 mt-2 text-muted small fw-bold">Logo Preview</p>
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-4 bg-white rounded-3 border border-dashed text-center">
                                                                        <p className="text-muted small mb-0">No logo uploaded yet</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 mt-4 text-end">
                                                    <button type="submit" className="btn btn-dark btn-lg px-5 fw-bold shadow rounded-pill" disabled={wizardLoading}>
                                                        {wizardLoading ? (
                                                            <><span className="spinner-border spinner-border-sm me-2"></span> SAVING...</>
                                                        ) : (
                                                            <><i className="ri-arrow-right-line me-2"></i> SAVE & CONTINUE</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}

                                    {wizardStep === 2 && (
                                        <form onSubmit={handleStepSubmit}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <h5 className="fw-bold mb-3 text-dark">Step 2: Complete KYC Documents</h5>
                                                    <hr className="my-3 text-muted opacity-25" />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-muted small">DOCUMENT TYPE</label>
                                                    <select className="form-select py-2" name="kyc_type" value={wizardFormData.kyc_type} onChange={handleInputChange} required>
                                                        <option value="">Choose Document...</option>
                                                        <option value="GST Certificate">GST Certificate</option>
                                                        <option value="Company PAN Card">Company PAN Card</option>
                                                        <option value="Business License">Business License</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold text-muted small">DOCUMENT NUMBER</label>
                                                    <input type="text" className="form-control py-2" name="kyc_doc_number" value={wizardFormData.kyc_doc_number} onChange={handleInputChange} placeholder="Enter ID number" required />
                                                </div>

                                                {wizardFormData.kyc_type === 'Other' && (
                                                    <div className="col-12">
                                                        <label className="form-label fw-bold text-muted small">SPECIFY DOCUMENT NAME</label>
                                                        <input type="text" className="form-control py-2" name="kyc_other_document" value={wizardFormData.kyc_other_document} onChange={handleInputChange} placeholder="Ex: Trade License" required />
                                                    </div>
                                                )}

                                                <div className="col-12 mt-4">
                                                    <div className="upload-container position-relative">
                                                        <input type="file" className="form-control d-none" id="wizardKycUpload" onChange={handleKycFileChange} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" />
                                                        <label htmlFor="wizardKycUpload" className="d-block p-5 border-2 border-dashed rounded-4 text-center cursor-pointer bg-white transition-all">
                                                            <div className="avatar-md mx-auto mb-3">
                                                                <div className="avatar-title bg-light text-dark rounded-circle shadow-sm fs-24" style={{ width: '48px', height: '48px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <i className="ri-upload-cloud-2-line"></i>
                                                                </div>
                                                            </div>
                                                            <h5 className="fs-16 fw-bold mb-1 text-dark">Upload Document File</h5>
                                                            <p className="text-muted small mb-0">Drag and drop or browse to select files</p>
                                                            <div className="mt-3">
                                                                <span className="badge text-dark bg-light px-2 py-1" style={{ border: '1px solid #cbd5e1' }}>JPG, PNG, PDF or DOC (Max 5MB)</span>
                                                            </div>
                                                        </label>
                                                    </div>
                                                    {kycFile && (
                                                        <div className="alert alert-dark mt-3 border-0 shadow-sm d-flex align-items-center text-white bg-dark">
                                                            <i className="ri-checkbox-circle-fill fs-20 me-2"></i>
                                                            <span className="fw-medium">Selected: {kycFile.name}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-12 text-center">
                                                    {kycPreview && (
                                                        <div className="preview-frame bg-white p-3 rounded-3 shadow-sm border border-light d-inline-block">
                                                            {typeof kycPreview === 'string' && kycPreview.toLowerCase().endsWith('.pdf') ? (
                                                                <div>
                                                                    <i className="ri-file-pdf-fill fs-1 text-danger"></i>
                                                                    <p className="mt-2 fw-bold text-dark small">PDF Verification File</p>
                                                                    <a href={kycPreview} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-dark rounded-pill px-4">
                                                                        <i className="ri-eye-line me-1"></i> VIEW FILE
                                                                    </a>
                                                                </div>
                                                            ) : typeof kycPreview === 'string' && (kycPreview.toLowerCase().endsWith('.doc') || kycPreview.toLowerCase().endsWith('.docx')) ? (
                                                                <div>
                                                                    <i className="ri-file-word-fill fs-1 text-primary"></i>
                                                                    <p className="mt-2 fw-bold text-dark small">Word Document File</p>
                                                                    <a href={kycPreview} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-dark rounded-pill px-4">
                                                                        <i className="ri-download-line me-1"></i> DOWNLOAD FILE
                                                                    </a>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <img src={kycPreview} className="img-fluid rounded shadow-sm" alt="KYC Proof" style={{ maxHeight: '150px' }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-12 mt-4 d-flex justify-content-between">
                                                    <button type="button" className="btn btn-light btn-lg px-4 fw-bold shadow rounded-pill" onClick={handlePrevStep}>
                                                        <i className="ri-arrow-left-line me-2"></i> BACK
                                                    </button>
                                                    <button type="submit" className="btn btn-dark btn-lg px-5 fw-bold shadow rounded-pill" disabled={wizardLoading}>
                                                        {wizardLoading ? (
                                                            <><span className="spinner-border spinner-border-sm me-2"></span> SAVING...</>
                                                        ) : (
                                                            <><i className="ri-arrow-right-line me-2"></i> SAVE & CONTINUE</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}

                                    {wizardStep === 3 && (
                                        <form onSubmit={handleStepSubmit}>
                                            <div className="row g-4">
                                                <div className="col-12">
                                                    <h5 className="fw-bold mb-3 text-dark">Step 3: Setup Business Categories</h5>
                                                    <hr className="my-3 text-muted opacity-25" />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-bold text-muted small mb-3">SELECT YOUR BUSINESS SERVICES</label>
                                                    <div className="d-flex flex-wrap gap-3">
                                                        {["Holidays", "Hotels", "Cabs", "Tirth Yatra", "Medical Tourism", "Other"].map(cat => (
                                                            <div key={cat} className="form-check form-check-inline border p-3 rounded-4 shadow-sm" style={{
                                                                minWidth: '150px',
                                                                backgroundColor: wizardFormData.business_categories.includes(cat) ? '#f8fafc' : '#fff',
                                                                borderColor: wizardFormData.business_categories.includes(cat) ? '#0f172a' : '#cbd5e1',
                                                                transition: 'all 0.2s',
                                                                cursor: 'pointer'
                                                            }}
                                                                onClick={() => handleCategoryChange(cat, !wizardFormData.business_categories.includes(cat))}
                                                            >
                                                                <input className="form-check-input me-2" type="checkbox" id={`cat_${cat}`} checked={wizardFormData.business_categories.includes(cat)} onChange={(e) => { }} style={{ pointerEvents: 'none' }} />
                                                                <label className="form-check-label fw-bold text-dark cursor-pointer mb-0" htmlFor={`cat_${cat}`} style={{ pointerEvents: 'none' }}>
                                                                    {cat}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {wizardFormData.business_categories.includes('Other') && (
                                                    <div className="col-12 mt-3">
                                                        <label className="form-label fw-bold text-muted small">SPECIFY OTHER SERVICE</label>
                                                        <input type="text" className="form-control py-2" name="other_category" value={wizardFormData.other_category} onChange={handleInputChange} placeholder="Ex: Tour Guides, Flight Booking, etc." required />
                                                    </div>
                                                )}

                                                <div className="col-12 mt-4 d-flex justify-content-between">
                                                    <button type="button" className="btn btn-light btn-lg px-4 fw-bold shadow rounded-pill" onClick={handlePrevStep}>
                                                        <i className="ri-arrow-left-line me-2"></i> BACK
                                                    </button>
                                                    <button type="submit" className="btn btn-success btn-lg px-5 fw-bold shadow rounded-pill" disabled={wizardLoading}>
                                                        {wizardLoading ? (
                                                            <><span className="spinner-border spinner-border-sm me-2"></span> SUBMITTING...</>
                                                        ) : (
                                                            <><i className="ri-check-double-line me-2"></i> COMPLETE REGISTRATION</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            ) : (
                // NORMAL DASHBOARD
                <>
                    <ActiveSubscriptionSummary />

                    {/* <Row>
                        <Col xs={12}>
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between mb-4">
                                <div>
                                    <h4 className="mb-sm-0 fw-bold" style={{ fontSize: '1.5rem', color: '#334155' }}>Vendor Dashboard</h4>
                                    <p className="text-muted mb-0">Track your business performance in real-time.</p>
                                </div>

                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0 bg-light p-2 px-3 rounded-pill">
                                        <li className="breadcrumb-item text-primary">Mangoloops</li>
                                        <li className="breadcrumb-item active">Dashboard</li>
                                    </ol>
                                </div>
                            </div>
                        </Col>
                    </Row> */}

                    <Row className="mb-4">
                        {statCards.map((card, index) => (
                            <Col xs={12} sm={6} lg={4} key={index} className="dashboard-stat-col mb-4">
                                <Card className="border-0 h-100 shadow-sm" style={{
                                    borderRadius: '16px',
                                    background: '#ffffff',
                                    transition: 'transform 0.2s ease-in-out',
                                    cursor: 'pointer'
                                }}
                                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <CardBody className="p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="avatar-md flex-shrink-0">
                                                <span className="avatar-title rounded-circle fs-2 shadow-sm" style={{ background: card.gradient, color: '#fff', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className={card.icon}></i>
                                                </span>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <p className="text-muted fw-medium mb-1 text-uppercase fs-11" style={{ letterSpacing: '0.05em' }}>{card.title}</p>
                                                <h2 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
                                                    {loading ? <span className="spinner-border spinner-border-sm" role="status"></span> : card.value}
                                                </h2>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center pt-2">
                                            <span className="badge px-3 py-2 rounded-pill fw-medium" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                {card.badge}
                                            </span>
                                            <div className="ms-auto">
                                                <i className="ri-arrow-right-up-line fs-5 text-success"></i>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Row>
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                                <CardBody className="p-4">
                                    <div className="d-flex align-items-center mb-3 flex-wrap" style={{ justifyContent: 'space-between' }}>
                                        <h5 className="card-title mb-0 flex-grow-1 fw-bold p-2" style={{ color: '#334155'  }}>Top 10 Leads</h5>
                                        <div className="flex-shrink-0">
                                            <Nav pills className="nav-success flex-nowrap overflow-auto" style={{ gap: '5px' }}>
                                                {['all', 'hotels', 'holidays', 'cab', 'doctor', 'hospital'].map((tab) => (
                                                    <NavItem key={tab}>
                                                        <NavLink
                                                            style={{ cursor: 'pointer', fontSize: '12px', padding: '5px 8px', borderRadius: '20px' }}
                                                            className={classnames({ active: activeTab === tab })}
                                                            onClick={() => handleTabChange(tab)}
                                                        >
                                                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                                        </NavLink>
                                                    </NavItem>
                                                ))}
                                            </Nav>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <Table className="align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th scope="col" style={{ borderRadius: '10px 0 0 0' }}>Customer Name</th>
                                                    <th scope="col">Mobile</th>
                                                    <th scope="col">Category</th>
                                                    <th scope="col">Date</th>
                                                    <th scope="col" style={{ borderRadius: '0 10px 0 0' }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {enquiriesLoading ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-5">
                                                            <div className="spinner-border text-primary" role="status"></div>
                                                        </td>
                                                    </tr>
                                                ) : enquiries.length > 0 ? (
                                                    enquiries.map((enq, idx) => (
                                                        <tr key={idx}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="flex-shrink-0 me-2">
                                                                        <div className="avatar-xs">
                                                                            <div className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                                                {idx + 1}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-grow-1 fw-medium text-capitalize">
                                                                        <Link to={`/admin/my-leads/${enq.id}`} className="text-reset text-decoration-none hover-primary">
                                                                            {enq.name}
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{enq.mobile}</td>
                                                            <td className="text-capitalize">{enq.type.replace(/_/g, " ") || 'NA'}</td>
                                                            <td>{formatDate(enq.created_at)}</td>
                                                            <td>
                                                                <span className="badge bg-success-subtle text-success">{enq.status}</span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-4 text-muted">No recent leads found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '16px' }}>
                                <CardBody className="p-0">
                                    <div className="p-4" style={{ background: '#1e293b' }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <h5 className="text-white mb-0 fw-bold">City-wise Lead Count</h5>
                                            <i className="ri-map-pin-time-line text-white-50 fs-20"></i>
                                        </div>
                                        <div className="input-group input-group-sm shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                                            <Flatpickr
                                                className="form-control border-0 bg-white text-dark"
                                                value={dateRange}
                                                options={{
                                                    mode: "range",
                                                    dateFormat: "d M, Y",
                                                }}
                                                onChange={onDateChange}
                                                placeholder="Select Date Range"
                                            />
                                            <span className="input-group-text bg-white border-0 text-muted">
                                                <i className="ri-calendar-event-line"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <div className="vstack gap-2" style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '5px' }}>
                                            {cityStats.length > 0 ? (
                                                cityStats.map((city, index) => (
                                                    <div key={index} className="d-flex align-items-center p-2" style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                                                        <div className="avatar-xs flex-shrink-0 me-2">
                                                            <div className="avatar-title rounded bg-soft-light text-muted fs-14">
                                                                <i className="ri-map-pin-2-line"></i>
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-0 fw-bold fs-13" style={{ color: '#334155' }}>{city.city}</h6>
                                                        </div>
                                                        <div className="flex-shrink-0 text-end">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span className="fw-bold text-primary fs-14">{city.count}</span>
                                                                <small className="text-success fw-medium fs-11">Leads</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-map-pin-line display-4 text-muted opacity-25"></i>
                                                    <p className="text-muted mt-2">No city data available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};


export default Dashboard;
